import React, { useEffect, useState } from 'react';
import { apiGet, apiDel, apiUpload } from '../../lib/api';
import { toast } from 'sonner';
import { Upload, Trash2, FileText, Download } from 'lucide-react';
import { fmtDate, BTN_PRIMARY, BTN_SECONDARY, normalizeProjects, INSURANCE_TYPES } from './complianceShared';

const STATUTORY_TYPES = ['Labour License', 'BOCW Registration', 'Pollution Certificate'];

export default function ComplianceVault() {
  const [docs, setDocs] = useState([]);
  const [projects, setProjects] = useState([]);
  const [projectFilter, setProjectFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [policyFilter, setPolicyFilter] = useState('');
  const [uploadModal, setUploadModal] = useState(false);
  const [records, setRecords] = useState([]);
  const [upForm, setUpForm] = useState({ compliance_type: 'insurance', compliance_id: '', project_id: '', file: null });

  const load = async () => {
    try {
      const params = {};
      if (projectFilter) params.project_id = projectFilter;
      if (typeFilter) params.compliance_type = typeFilter;
      if (policyFilter) params.policy_type = policyFilter;
      const r = await apiGet('/compliance/vault/documents', params);
      setDocs(r.items || []);
    } catch (e) { console.error(e); }
  };
  useEffect(() => { load(); }, [projectFilter, typeFilter, policyFilter]);
  useEffect(() => { apiGet('/projects').then(r => setProjects(normalizeProjects(r))).catch(() => {}); }, []);

  // Load compliance records for the upload modal's record picker
  useEffect(() => {
    if (!uploadModal) return;
    const ep = upForm.compliance_type === 'insurance' ? '/compliance/insurance' : '/compliance/statutory';
    apiGet(ep).then(r => setRecords(r.items || [])).catch(() => setRecords([]));
  }, [uploadModal, upForm.compliance_type]);

  const removeDoc = async (d) => {
    if (!window.confirm(`Delete document "${d.filename}"?`)) return;
    try {
      await apiDel(`/compliance/${d.compliance_type}/${d.compliance_id}/documents/${d.id}`);
      toast.success('Document deleted'); load();
    } catch (e) { toast.error('Delete failed'); }
  };

  const submitUpload = async () => {
    if (!upForm.compliance_id || !upForm.file) { toast.error('Select a compliance record and a file'); return; }
    const fd = new FormData();
    fd.append('file', upForm.file);
    if (upForm.project_id) fd.append('project_id', upForm.project_id);
    try {
      await apiUpload(`/compliance/${upForm.compliance_type}/${upForm.compliance_id}/documents`, fd);
      toast.success('Document uploaded');
      setUploadModal(false);
      setUpForm({ compliance_type: 'insurance', compliance_id: '', project_id: '', file: null });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Upload failed'); }
  };

  const policyTypeOptions = typeFilter === 'statutory'
    ? STATUTORY_TYPES
    : typeFilter === 'insurance'
      ? INSURANCE_TYPES.map(t => t.fullLabel)
      : [];

  return (
    <div data-testid="compliance-vault-page">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">People &amp; HR · Compliance</div>
          <h1 className="font-display text-3xl font-extrabold tracking-tight">Compliance Vault</h1>
          <p className="text-sm text-slate-600 mt-1">All insurance &amp; statutory documents — filterable by project.</p>
        </div>
        <button onClick={() => setUploadModal(true)} className={`${BTN_PRIMARY} flex items-center gap-2`} data-testid="vault-upload-btn">
          <Upload size={14} /> Upload Document
        </button>
      </div>

      <div className="flex items-center gap-3 mb-4 flex-wrap" data-testid="vault-filters-bar">
        <select value={projectFilter} onChange={e => setProjectFilter(e.target.value)}
                className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm" data-testid="vault-project-filter">
          <option value="">All Projects</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select value={typeFilter} onChange={e => { setTypeFilter(e.target.value); setPolicyFilter(''); }}
                className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm" data-testid="vault-type-filter">
          <option value="">All Types</option>
          <option value="insurance">Insurance</option>
          <option value="statutory">Statutory</option>
        </select>
        {policyTypeOptions.length > 0 && (
          <select value={policyFilter} onChange={e => setPolicyFilter(e.target.value)}
                  className="border border-slate-300 rounded-sm px-2 py-1.5 text-sm" data-testid="vault-policy-filter">
            <option value="">All {typeFilter === 'insurance' ? 'Policy' : 'Statutory'} Types</option>
            {policyTypeOptions.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        )}
        <span className="text-xs text-slate-400 ml-auto">{docs.length} document(s)</span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3" data-testid="vault-document-grid">
        {docs.length === 0 && (
          <div className="col-span-full bg-white border border-slate-200 rounded-sm p-10 text-center text-sm text-slate-400">
            No compliance documents found for the selected filters.
          </div>
        )}
        {docs.map(d => (
          <div key={`${d.compliance_id}-${d.id}`} className="bg-white border border-slate-200 rounded-sm p-4" data-testid="vault-doc-card">
            <div className="flex items-start gap-3">
              <FileText size={18} className="text-[#3b4872] mt-0.5 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm text-slate-800 truncate" title={d.filename}>{d.filename}</div>
                <div className="text-xs text-slate-500 mt-1">
                  <span className={`px-1.5 py-0.5 rounded-sm text-[10px] font-bold ${d.compliance_type === 'insurance' ? 'bg-sky-100 text-sky-700' : 'bg-indigo-100 text-indigo-700'}`}>
                    {d.compliance_type === 'insurance' ? 'Insurance' : 'Statutory'}
                  </span>
                  <span className="ml-1.5">{d.type_label}</span>
                </div>
                {d.ref_number && <div className="text-xs text-slate-400 mt-0.5">№ {d.ref_number}</div>}
                <div className="text-xs text-slate-400 mt-0.5">{fmtDate(d.upload_date)}{d.project_name ? ` · ${d.project_name}` : ''}</div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 mt-3 pt-3 border-t border-slate-100">
              <a href={d.url} target="_blank" rel="noreferrer" className={`${BTN_SECONDARY} flex items-center gap-1`} data-testid="vault-doc-download">
                <Download size={11} /> Download
              </a>
              <button onClick={() => removeDoc(d)} className="text-slate-400 hover:text-rose-600 p-1.5" data-testid="vault-doc-delete">
                <Trash2 size={13} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {uploadModal && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setUploadModal(false)}>
          <div className="bg-white rounded-sm border border-slate-200 w-full max-w-md p-6" onClick={e => e.stopPropagation()} data-testid="vault-upload-modal">
            <h2 className="font-display text-lg font-bold mb-4">Upload Compliance Document</h2>
            <div className="space-y-3 text-sm">
              <label className="block"><span className="kpi-overline">Compliance Type</span>
                <select value={upForm.compliance_type} onChange={e => setUpForm(f => ({ ...f, compliance_type: e.target.value, compliance_id: '' }))}
                        className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="upload-compliance-type-select">
                  <option value="insurance">Insurance</option>
                  <option value="statutory">Statutory</option>
                </select></label>
              <label className="block"><span className="kpi-overline">Compliance Record</span>
                <select value={upForm.compliance_id} onChange={e => setUpForm(f => ({ ...f, compliance_id: e.target.value }))}
                        className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="upload-record-select">
                  <option value="">— Select record —</option>
                  {records.map(r => (
                    <option key={r.id} value={r.id}>
                      {upForm.compliance_type === 'insurance'
                        ? `${r.policy_type} · ${r.policy_number || 'No number'}`
                        : `${r.statutory_type} · ${r.project_name || r.project_id}`}
                    </option>
                  ))}
                </select></label>
              <label className="block"><span className="kpi-overline">Project (optional)</span>
                <select value={upForm.project_id} onChange={e => setUpForm(f => ({ ...f, project_id: e.target.value }))}
                        className="mt-1 w-full border border-slate-300 rounded-sm px-2 py-1.5" data-testid="upload-project-select">
                  <option value="">— None —</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select></label>
              <label className="block"><span className="kpi-overline">File</span>
                <input type="file" onChange={e => setUpForm(f => ({ ...f, file: e.target.files?.[0] || null }))}
                       className="mt-1 w-full text-sm" data-testid="upload-file-input" /></label>
            </div>
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setUploadModal(false)} className={BTN_SECONDARY}>Cancel</button>
              <button onClick={submitUpload} className={BTN_PRIMARY} data-testid="upload-submit-btn">Upload</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
